/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Detector engine - runs all detectors in parallel and aggregates results
 */
import type { DetectorResult } from '../types/index.js';
import { detectors } from '../detectors/index.js';
import { logger } from '../utils/logger.js';

/**
 * Run all detectors in parallel and return sorted results
 */
export const runDetectors = async (
  cwd: string,
  verbose = false
): Promise<DetectorResult[]> => {
  logger.debug(`Running ${detectors.length} detectors in parallel...`, verbose);

  const results = await Promise.all(
    detectors.map(async (detector) => {
      try {
        const result = await detector.detect(cwd);
        logger.debug(
          `${detector.name}: confidence ${result.confidence}`,
          verbose
        );
        return result;
      } catch (error) {
        logger.debug(
          `${detector.name} failed: ${error instanceof Error ? error.message : String(error)}`,
          verbose
        );
        return {
          detectorName: detector.name,
          confidence: 0,
        } as DetectorResult;
      }
    })
  );

  // Sort by confidence (highest first) and filter out zero-confidence results
  return results
    .filter((result) => result.confidence > 0)
    .sort((a, b) => b.confidence - a.confidence);
};

/**
 * Get the best detection result
 */
export const getBestDetection = (results: DetectorResult[]): DetectorResult | null => {
  return results[0] || null;
};

